/**
 * QuestTile - Expandable quest progress tile
 *
 * Compact tile showing one active quest's status with per-objective tracking.
 * Used by QuestDock (global dock above tab bar).
 */

import { useState, useMemo } from 'react';
import { ActiveQuest, QuestDefinition, QuestRewardType } from '@/types';

interface QuestTileProps {
  activeQuest: ActiveQuest;
  definition: QuestDefinition;
  onTurnIn: (questId: string) => void;
  compact?: boolean;
  onNavigate?: () => void;
  navigateLabel?: string;
  navigateIcon?: string;
  onAbandon?: (questId: string) => void;
}

/**
 * Format reward summary string for display
 */
function formatRewardSummary(definition: QuestDefinition): string {
  return definition.rewards
    .map((r) => {
      switch (r.type) {
        case QuestRewardType.CURRENCY:
          return `+${r.value} gold`;
        case QuestRewardType.INVENTORY_EXPANSION:
          return `+${r.value} inventory`;
        case QuestRewardType.EXPERIENCE:
          return `+${r.value} XP`;
        default:
          return r.description || '';
      }
    })
    .filter(Boolean)
    .join(', ');
}

export function QuestTile({ activeQuest, definition, onTurnIn, compact, onNavigate, navigateLabel, navigateIcon, onAbandon }: QuestTileProps) {
  const [expanded, setExpanded] = useState(false);
  const [confirmingAbandon, setConfirmingAbandon] = useState(false);
  const allComplete = activeQuest.completed && !activeQuest.claimedRewards;

  const rewardSummary = useMemo(() => formatRewardSummary(definition), [definition]);

  // Match objectives from definition (for descriptions) with progress
  const objectives = useMemo(() => {
    return activeQuest.objectives.map((progress, i) => ({
      description: definition.objectives[i]?.description ?? progress.objectiveId,
      currentValue: progress.currentValue,
      targetValue: progress.targetValue,
      completed: progress.completed,
    }));
  }, [activeQuest.objectives, definition.objectives]);

  const textSize = compact ? 'text-xs' : 'text-sm';
  const headerSize = compact ? 'text-xs' : 'text-sm';

  if (allComplete) {
    // === ALL COMPLETE STATE: green glow with turn-in button ===
    return (
      <div className="bg-green-900/30 border-2 border-green-500 rounded-lg p-3 transition-all">
        {/* Header */}
        <div className="flex items-center gap-2 mb-2">
          <span className="text-green-400 text-lg">✅</span>
          <h4 className={`${headerSize} font-bold text-green-300 flex-1`}>
            {definition.isTutorial && <span className="text-yellow-400">⭐ </span>}
            {definition.name}
          </h4>
        </div>

        {/* Turn-in button */}
        <button
          onClick={() => onTurnIn(activeQuest.questId)}
          className="w-full bg-green-600 hover:bg-green-500 text-white px-3 py-2 rounded-lg text-sm font-bold transition-colors cursor-pointer"
        >
          Turn in Quest ({rewardSummary})
        </button>
      </div>
    );
  }

  // === IN-PROGRESS STATE: objectives + optional expanded view ===
  return (
    <div className="bg-gray-800/80 border border-gray-700 rounded-lg p-3 transition-all">
      {/* Clickable header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center justify-between w-full text-left cursor-pointer"
      >
        <h4 className={`${headerSize} font-bold text-white flex-1`}>
          {definition.isTutorial && <span className="text-yellow-400">⭐ </span>}
          {definition.name}
        </h4>
        <span className="text-gray-500 text-xs ml-2">
          {expanded ? '▲' : '▼'}
        </span>
      </button>

      {/* Expanded: description + rewards */}
      {expanded && (
        <div className="mt-2 mb-2">
          <p className="text-gray-400 text-xs italic mb-2">{definition.description}</p>
          <p className="text-yellow-400/80 text-xs">Rewards: {rewardSummary}</p>
        </div>
      )}

      {/* Objectives */}
      <div className={`mt-2 space-y-1 ${compact ? 'space-y-0.5' : ''}`}>
        {objectives.map((obj) => (
          <div
            key={obj.description}
            className={`flex items-center justify-between ${textSize}`}
          >
            <div className="flex items-center gap-1.5 flex-1 min-w-0">
              <span className={obj.completed ? 'text-green-400' : 'text-gray-500'}>
                {obj.completed ? '✅' : '○'}
              </span>
              <span
                className={`truncate ${
                  obj.completed ? 'text-green-300 line-through' : 'text-gray-300'
                }`}
              >
                {obj.description}
              </span>
            </div>
            <span
              className={`ml-2 tabular-nums ${
                obj.completed ? 'text-green-400' : 'text-gray-400'
              }`}
            >
              {obj.currentValue}/{obj.targetValue}
            </span>
          </div>
        ))}
      </div>

      {/* Action buttons */}
      {(onNavigate || onAbandon) && (
        <div className="mt-2 flex items-center gap-2">
          {onNavigate && (
            <button
              onClick={(e) => { e.stopPropagation(); onNavigate(); }}
              className="flex-1 bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold px-3 py-1.5 rounded-md transition-colors cursor-pointer"
              aria-label={navigateLabel || 'Navigate to quest location'}
            >
              {navigateIcon && <span className="mr-1">{navigateIcon}</span>}
              {navigateLabel || 'Go'}
            </button>
          )}
          {onAbandon && !confirmingAbandon && (
            <button
              onClick={(e) => { e.stopPropagation(); setConfirmingAbandon(true); }}
              className="text-xs text-red-400/60 hover:text-red-400 px-2 py-1.5 rounded-md transition-colors cursor-pointer"
              aria-label="Abandon quest"
            >
              Abandon
            </button>
          )}
          {onAbandon && confirmingAbandon && (
            <div className="flex items-center gap-1">
              <button
                onClick={(e) => { e.stopPropagation(); onAbandon(activeQuest.questId); setConfirmingAbandon(false); }}
                className="bg-red-600 hover:bg-red-500 text-white text-xs font-semibold px-2 py-1.5 rounded-md transition-colors cursor-pointer"
                aria-label="Confirm abandon quest"
              >
                Yes, abandon
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setConfirmingAbandon(false); }}
                className="text-xs text-gray-400 hover:text-gray-300 px-2 py-1.5 rounded-md transition-colors cursor-pointer"
                aria-label="Cancel abandon"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
